import React, { useMemo, useState } from 'react';
import { Select, Text } from 'src/@vymo/ui/atoms';
import SwaggerUI from 'swagger-ui-react';
import { Keys, locale } from 'src/i18n';
import { SWAGGER_API_SPECS } from './apiSpecs';
import styles from './index.module.scss';
import 'swagger-ui-react/swagger-ui.css';

const getDefaultServers = () => {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : undefined;
  const base = [
    {
      url: 'https://revyre-uat.lms.getvymo.com',
      description: 'Revyre UAT',
    },
    {
      url: 'https://staging.lms.getvymo.com',
      description: 'Staging',
    },
    { url: '/', description: 'Relative to current host' },
  ];
  const seen = new Set(base.map((s) => s.url));
  const withOrigin =
    origin && !seen.has(origin)
      ? [{ url: origin, description: 'Current origin (this app)' }, ...base]
      : base;
  return withOrigin;
};

export default function SwaggerViewer() {
  const [selectedKey, setSelectedKey] = useState<string>(
    () => SWAGGER_API_SPECS[0]?.name ?? '',
  );

  const selectedSpec = useMemo(
    () => SWAGGER_API_SPECS.find((s) => s.name === selectedKey),
    [selectedKey],
  );

  const specWithServers = useMemo(() => {
    if (!selectedSpec?.spec) return selectedSpec?.spec;
    const servers = getDefaultServers();
    return {
      ...selectedSpec.spec,
      servers:
        servers.length > 0 ? servers : (selectedSpec.spec as any).servers,
    };
  }, [selectedSpec]);

  const specOptions = useMemo(
    () =>
      SWAGGER_API_SPECS.map((s) => ({
        value: s.name,
        name: s.name,
        code: s.name,
      })),
    [],
  );

  if (SWAGGER_API_SPECS.length === 0) {
    return (
      <div className={styles.swaggerViewer}>
        <div className={styles.swaggerViewer__empty}>
          <Text type="h4">{locale(Keys.SWAGGER_API_DOCS)}</Text>
          <Text type="description">
            No API specs configured yet. Add your OpenAPI/Swagger specs in{' '}
            <code>src/modules/dashboard/swagger/apiSpecs.ts</code>.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.swaggerViewer}>
      <div className={styles.swaggerViewer__header}>
        <Text type="h4">{locale(Keys.SWAGGER_API_DOCS)}</Text>
        {specOptions.length > 1 && (
          <div className={styles.swaggerViewer__selector}>
            <Select
              placeholder="Select API"
              options={specOptions}
              value={selectedKey}
              onChange={([option]) => setSelectedKey(option?.value ?? '')}
              data-test-id="swagger-api-select"
            />
          </div>
        )}
      </div>
      <div className={styles.swaggerViewer__content}>
        {selectedSpec &&
          // eslint-disable-next-line no-nested-ternary
          (selectedSpec.url ? (
            <SwaggerUI url={selectedSpec.url} />
          ) : specWithServers ? (
            <SwaggerUI spec={specWithServers} />
          ) : null)}
      </div>
    </div>
  );
}
